import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [logoutPrompt, setLogoutPrompt] = useState(null);
  const logoutResolver = useRef(null);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — let AuthCallback exchange session_id first
    if (window.location.hash?.includes("session_id=")) return;
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password, companyCode) => {
    const { data } = await api.post("/auth/login", { email, password, company_code: companyCode || null });
    setUser(data);
    return data;
  };

  const finishLogout = async (checkOut) => {
    if (checkOut) {
      try { await api.post("/attendance/checkout", { note: "" }); }
      catch (e) {
        window.alert(e.response?.data?.detail || "Check-out failed. You are still signed in.");
        return;
      }
    }
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout request failed", e); }
    setUser(false);
    setLogoutPrompt(null);
    logoutResolver.current?.(true);
    logoutResolver.current = null;
  };

  const logout = async () => {
    let status;
    try { status = (await api.get("/attendance/status")).data; } catch (e) { /* continue with ordinary logout */ }
    if (!status?.checked_in) {
      await finishLogout(false);
      return true;
    }
    return new Promise((resolve) => {
      logoutResolver.current = resolve;
      setLogoutPrompt(status);
    });
  };

  const cancelLogout = () => {
    setLogoutPrompt(null);
    logoutResolver.current?.(false);
    logoutResolver.current = null;
  };

  const record = logoutPrompt?.record || {};
  const checkinTime = record.check_in ? new Date(record.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  const hours = record.check_in ? ((record.sessions || [{ check_in: record.check_in, check_out: null }]).reduce((sum, session) => {
    if (!session.check_in) return sum;
    const end = session.check_out ? new Date(session.check_out).getTime() : Date.now();
    return sum + Math.max(0, end - new Date(session.check_in).getTime());
  }, 0) / 3600000).toFixed(2) : "0.00";
  const logoutView = logoutPrompt && (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-overlay p-4" role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="attendance-logout-title" className="w-full max-w-md border bg-background p-5 shadow-xl">
        <h2 id="attendance-logout-title" className="font-heading text-lg font-semibold">You have not checked out</h2>
        <p className="mt-2 text-sm text-muted-foreground">Checked in {checkinTime}; approximately {hours} hours worked.</p>
        {logoutPrompt.open_jobs_today > 0 && <p className="mt-2 text-sm text-warning-foreground">You have {logoutPrompt.open_jobs_today} open job(s) today.</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={cancelLogout} className="h-9 rounded-md border px-3 text-sm">Cancel</button>
          <button type="button" onClick={() => finishLogout(false)} className="h-9 rounded-md border px-3 text-sm">Logout only</button>
          <button type="button" onClick={() => finishLogout(true)} className="h-9 rounded-md bg-success px-3 text-sm font-semibold text-inverse">Check Out &amp; Logout</button>
        </div>
      </section>
    </div>
  );

  return <AuthCtx.Provider value={{ user, login, logout, setUser }}>
    {children}
    {logoutView}
  </AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
